// src/login/Register.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Register.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const EDUCATION_OPTIONS = [
  '10th Pass',
  '12th Pass',
  'Diploma',
  'Bachelor of Arts (BA)',
  'Bachelor of Science (BSc)',
  'Bachelor of Commerce (BCom)',
  'Bachelor of Engineering (BE)',
  'Bachelor of Technology (BTech)',
  'Bachelor of Computer Applications (BCA)',
  'Bachelor of Business Administration (BBA)',
  'Master of Arts (MA)',
  'Master of Science (MSc)',
  'Master of Commerce (MCom)',
  'Master of Engineering (ME)',
  'Master of Technology (MTech)',
  'Master of Computer Applications (MCA)',
  'Master of Business Administration (MBA)',
  'Doctor of Philosophy (PhD)',
  'Other'
];

const NOTICE_PERIOD_OPTIONS = [
  { value: '', label: 'Select Notice Period' },
  { value: '0', label: 'Immediate (0 days)' },
  { value: '7', label: '7 days' },
  { value: '15', label: '15 days' },
  { value: '30', label: '30 days' },
  { value: '45', label: '45 days' },
  { value: '60', label: '60 days' },
  { value: '90', label: '90 days' },
  { value: '120', label: '120 days' }
];

const Register = () => {
  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    location: '',
    preferred_location: '',
    experience_years: '',
    education: '',
    skills: '',
    department: '',
    current_company: '',
    current_salary: '',
    expected_salary: '',
    notice_period_days: '',
    linkedin_url: ''
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [resumeFileName, setResumeFileName] = useState('');

  const [otp, setOtp] = useState([
    '',
    '',
    '',
    '',
    '',
    ''
  ]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();

  const inputRefs = useRef([]);
  const errorRef = useRef(null);

  /*
   * Scroll automatically to error message
   */
  useEffect(() => {
    if (error && errorRef.current) {
      setTimeout(() => {
        errorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [error]);

  /*
   * OTP Timer
   */
  useEffect(() => {
    if (step === 2 && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    }

    if (timer === 0) {
      setCanResend(true);
    }
  }, [step, timer]);

  /*
   * Focus first OTP input
   */
  useEffect(() => {
    if (step === 2 && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [step]);

  /*
   * Normal input change
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    setError('');
  };

  /*
   * Select change
   */
  const handleSelectChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));

    setError('');
  };

  /*
   * Mobile number formatting
   *
   * Display:
   * 98765 43210
   *
   * Actual stored value:
   * 9876543210
   */
  const formatMobileNumber = (value) => {
    const cleaned = value.replace(/\D/g, '').slice(0, 10);

    if (cleaned.length <= 5) {
      return cleaned;
    }

    return `${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
  };

  /*
   * Mobile number change
   */
  const handleMobileChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');

    if (raw.length <= 10) {
      setFormData((prev) => ({
        ...prev,
        mobile: raw
      }));

      setError('');
    }
  };

  /*
   * Resume upload
   */
  const handleResumeChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a PDF, DOC, or DOCX file');
      return;
    }

    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }

    setResumeFile(file);
    setResumeFileName(file.name);
    setError('');
  };

  /*
   * Validate Indian mobile number
   */
  const validateMobileNumber = (mobile) => {
    /*
     * Must:
     * - contain exactly 10 digits
     * - start with 6, 7, 8 or 9
     */

    if (!/^[6-9][0-9]{9}$/.test(mobile)) {
      return false;
    }

    /*
     * Reject obvious dummy numbers
     */
    if (
      mobile === '1234567890' ||
      mobile === '0123456789' ||
      /^(\d)\1{9}$/.test(mobile)
    ) {
      return false;
    }

    return true;
  };

  /*
   * Form validation
   */
  const validateForm = () => {
    const {
      name,
      email,
      mobile,
      password,
      confirmPassword,
      education
    } = formData;

    if (!name || !mobile || !password || !confirmPassword) {
      setError(
        'Name, Mobile, and Password are required'
      );
      return false;
    }

    if (!email) {
      setError('Email address is required');
      return false;
    }

    if (!resumeFile) {
      setError('Please upload your resume (Required)');
      return false;
    }

    if (name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return false;
    }

    /*
     * Proper Indian WhatsApp/mobile number
     */
    if (!validateMobileNumber(mobile)) {
      setError(
        'Please enter a valid 10-digit WhatsApp mobile number starting with 6, 7, 8, or 9'
      );
      return false;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/;

    if (!passwordRegex.test(password)) {
      setError(
        'Password must contain at least one uppercase, one lowercase, and one number'
      );
      return false;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (!education) {
      setError('Please select your education qualification');
      return false;
    }

    return true;
  };

  /*
   * Register
   */
  const handleRegister = async (e) => {
    e.preventDefault();

    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/otp/request`,
        {
          mobile: formData.mobile,
          purpose: 'register'
        }
      );

      if (response.data.success) {
        const data = response.data.data;

        if (data.debugOtp) {
          console.log(
            '📱 Debug OTP:',
            data.debugOtp
          );

          alert(
            `📱 Your OTP is: ${data.debugOtp}\n\n(Development Mode Only)`
          );
        }

        setStep(2);
        setTimer(30);
        setCanResend(false);
        setError('');
      }
    } catch (err) {
      console.error(
        'Registration error:',
        err
      );

      setError(
        err.response?.data?.message ||
          'Failed to send OTP. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * OTP change
   */
  const handleOtpChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) {
      return;
    }

    const newOtp = [...otp];

    newOtp[index] = value;

    setOtp(newOtp);

    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  /*
   * OTP keyboard
   */
  const handleOtpKeyDown = (index, e) => {
    if (
      e.key === 'Backspace' &&
      !otp[index] &&
      index > 0
    ) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  /*
   * OTP paste
   */
  const handleOtpPaste = (e) => {
    const pasteData =
      e.clipboardData
        .getData('text')
        .replace(/\D/g, '');

    if (
      pasteData.length === 6 &&
      /^[0-9]{6}$/.test(pasteData)
    ) {
      const otpArray = pasteData.split('');

      setOtp(otpArray);

      setError('');

      if (inputRefs.current[5]) {
        inputRefs.current[5].focus();
      }
    }
  };

  /*
   * Verify OTP
   */
  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    const otpString = otp.join('');

    setError('');

    if (otpString.length !== 6) {
      setError(
        'Please enter the complete 6-digit OTP'
      );
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/otp/verify`,
        {
          mobile: formData.mobile,
          code: otpString,
          name: formData.name,
          email: formData.email || '',
          password: formData.password,
          purpose: 'register'
        }
      );

      if (response.data.success) {
        const data = response.data.data;

        localStorage.setItem(
          'token',
          data.token
        );

        localStorage.setItem(
          'userRole',
          data.user.role
        );

        localStorage.setItem(
          'userName',
          data.user.name
        );

        localStorage.setItem(
          'userEmail',
          data.user.email || ''
        );

        localStorage.setItem(
          'userMobile',
          data.user.mobile
        );

        localStorage.setItem(
          'isLoggedIn',
          'true'
        );

        localStorage.setItem(
          'userId',
          data.user.id
        );

        console.log(
          '✅ Registration Successful!',
          data.user
        );

        await updateCandidateProfile(
          data.token
        );

        navigate('/user/view-requirement');
      }
    } catch (err) {
      console.error(
        'OTP verification error:',
        err
      );

      setError(
        err.response?.data?.message ||
          'Invalid OTP. Please try again.'
      );

      setOtp([
        '',
        '',
        '',
        '',
        '',
        ''
      ]);

      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  /*
   * Update candidate profile
   */
  const updateCandidateProfile = async (token) => {
    try {
      const skillsArray = formData.skills
        ? formData.skills
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s)
        : [];

      const profileData = {
        name: formData.name,
        email: formData.email || '',
        location: formData.location || '',
        preferred_location:
          formData.preferred_location || '',
        experience_years:
          formData.experience_years
            ? parseFloat(
                formData.experience_years
              )
            : null,
        education:
          formData.education || '',
        skills: skillsArray,
        department:
          formData.department || '',
        current_company:
          formData.current_company || '',
        current_salary:
          formData.current_salary
            ? parseFloat(
                formData.current_salary
              )
            : null,
        expected_salary:
          formData.expected_salary
            ? parseFloat(
                formData.expected_salary
              )
            : null,
        notice_period_days:
          formData.notice_period_days
            ? parseInt(
                formData.notice_period_days
              )
            : null,
        linkedin_url:
          formData.linkedin_url || ''
      };

      console.log(
        '📤 Updating candidate profile:',
        profileData
      );

      const response = await axios.put(
        `${API_BASE_URL}/api/candidates/me`,
        profileData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log(
        '✅ Profile updated:',
        response.data
      );

      if (resumeFile) {
        const formDataUpload =
          new FormData();

        formDataUpload.append(
          'resume',
          resumeFile
        );

        await axios.post(
          `${API_BASE_URL}/api/candidates/me/resume`,
          formDataUpload,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type':
                'multipart/form-data'
            }
          }
        );

        console.log(
          '✅ Resume uploaded successfully'
        );
      }
    } catch (err) {
      console.error(
        '❌ Error updating profile:',
        err
      );
    }
  };

  /*
   * Resend OTP
   */
  const handleResendOtp = async () => {
    if (!canResend) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/otp/request`,
        {
          mobile: formData.mobile,
          purpose: 'register'
        }
      );

      if (response.data.success) {
        const data = response.data.data;

        setTimer(30);
        setCanResend(false);

        setOtp([
          '',
          '',
          '',
          '',
          '',
          ''
        ]);

        setError('');

        if (data.debugOtp) {
          console.log(
            '📱 New Debug OTP:',
            data.debugOtp
          );

          alert(
            `📱 Your new OTP is: ${data.debugOtp}\n\n(Development Mode Only)`
          );
        }

        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 100);
      }
    } catch (err) {
      console.error(
        'Resend OTP error:',
        err
      );

      setError(
        err.response?.data?.message ||
          'Failed to resend OTP. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">

      <div className="register-card">

        <div className="register-header">
          <h1>Create Account</h1>

          <p>
            {step === 1
              ? 'Register as a candidate'
              : 'Verify your mobile number with OTP'}
          </p>
        </div>

        <div className="register-steps">

          <div
            className={`step ${
              step >= 1 ? 'active' : ''
            }`}
          >
            <span className="step-number">
              1
            </span>

            <span className="step-label">
              Details
            </span>
          </div>

          <div
            className={`step-line ${
              step >= 2 ? 'active' : ''
            }`}
          />

          <div
            className={`step ${
              step >= 2 ? 'active' : ''
            }`}
          >
            <span className="step-number">
              2
            </span>

            <span className="step-label">
              Verify
            </span>
          </div>

        </div>

        {error && step === 1 && (
          <div
            ref={errorRef}
            className="error-message"
          >
            <i className="fas fa-exclamation-circle" />
            <span>{error}</span>
          </div>
        )}

        {step === 1 ? (

          <form
            onSubmit={handleRegister}
            className="register-form"
          >

            <div className="form-group">

              <label htmlFor="name">
                <i className="fas fa-user" />
                Full Name *
              </label>

              <input
                type="text"
                id="name"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />

            </div>

            <div className="form-group">

              <label htmlFor="email">
                <i className="fas fa-envelope" />
                Email Address *
              </label>

              <input
                type="email"
                id="email"
                name="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleInputChange}
                disabled={isLoading}
                autoComplete="email"
                required
              />

              <small>
                We'll send job updates to this email
              </small>

            </div>

            <div className="form-group">

              <label htmlFor="mobile">
                <i className="fab fa-whatsapp" />
                WhatsApp Mobile Number *
              </label>

              <input
                type="tel"
                id="mobile"
                name="mobile"
                placeholder="00000 00000"
                value={formatMobileNumber(
                  formData.mobile
                )}
                onChange={handleMobileChange}
                disabled={isLoading}
                maxLength={11}
                inputMode="numeric"
                autoComplete="tel"
                required
              />

              <small>Enter a valid 10-digit WhatsApp number to receive the OTP.</small>

            </div>

            <div className="form-group">

              <label htmlFor="password">
                <i className="fas fa-lock" />
                Password *
              </label>

              <div className="password-wrapper">

                <input
                  type={
                    showPassword
                      ? 'text'
                      : 'password'
                  }
                  id="password"
                  name="password"
                  placeholder="Min 8 chars with uppercase, lowercase, number"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                />

                <button
                  type="button"
                  className="toggle-password"
                  onClick={() =>
                    setShowPassword(
                      !showPassword
                    )
                  }
                  tabIndex="-1"
                >
                  <i
                    className={`fas ${
                      showPassword
                        ? 'fa-eye-slash'
                        : 'fa-eye'
                    }`}
                  />
                </button>

              </div>

              <small>
                Must contain uppercase, lowercase, and number
              </small>

            </div>

            <div className="form-group">

              <label htmlFor="confirmPassword">
                <i className="fas fa-check-circle" />
                Confirm Password *
              </label>

              <div className="password-wrapper">

                <input
                  type={
                    showConfirmPassword
                      ? 'text'
                      : 'password'
                  }
                  id="confirmPassword"
                  name="confirmPassword"
                  placeholder="Re-enter your password"
                  value={
                    formData.confirmPassword
                  }
                  onChange={handleInputChange}
                  disabled={isLoading}
                  required
                />

                <button
                  type="button"
                  className="toggle-password"
                  onClick={() =>
                    setShowConfirmPassword(
                      !showConfirmPassword
                    )
                  }
                  tabIndex="-1"
                >
                  <i
                    className={`fas ${
                      showConfirmPassword
                        ? 'fa-eye-slash'
                        : 'fa-eye'
                    }`}
                  />
                </button>

              </div>

            </div>

            <div className="form-row">

              <div className="form-group half">

                <label htmlFor="experience_years">
                  <i className="fas fa-briefcase" />
                  Experience (Years)
                </label>

                <input
                  type="number"
                  id="experience_years"
                  name="experience_years"
                  placeholder="e.g., 3"
                  value={
                    formData.experience_years
                  }
                  onChange={handleInputChange}
                  disabled={isLoading}
                  min="0"
                  step="0.5"
                />

              </div>

              <div className="form-group half">

                <label htmlFor="education">
                  <i className="fas fa-graduation-cap" />
                  Education *
                </label>

                <select
                  id="education"
                  name="education"
                  value={formData.education}
                  onChange={handleSelectChange}
                  disabled={isLoading}
                  className="education-select"
                  required
                >

                  <option value="">
                    Select your education
                  </option>

                  {EDUCATION_OPTIONS.map(
                    (edu) => (
                      <option
                        key={edu}
                        value={edu}
                      >
                        {edu}
                      </option>
                    )
                  )}

                </select>

              </div>

            </div>

            <div className="form-group">

              <label htmlFor="skills">
                <i className="fas fa-code" />
                Skills
              </label>

              <input
                type="text"
                id="skills"
                name="skills"
                placeholder="React, Node.js, Python (comma separated)"
                value={formData.skills}
                onChange={handleInputChange}
                disabled={isLoading}
              />

              <small>
                Enter skills separated by commas
              </small>

            </div>

            <div className="form-group">

              <label htmlFor="department">
                <i className="fas fa-building" />
                Department
              </label>

              <input
                type="text"
                id="department"
                name="department"
                placeholder="e.g., Engineering"
                value={formData.department}
                onChange={handleInputChange}
                disabled={isLoading}
              />

            </div>

            <div className="form-row">

              <div className="form-group half">

                <label htmlFor="current_company">
                  <i className="fas fa-building" />
                  Current Company
                </label>

                <input
                  type="text"
                  id="current_company"
                  name="current_company"
                  placeholder="Company name"
                  value={
                    formData.current_company
                  }
                  onChange={handleInputChange}
                  disabled={isLoading}
                />

              </div>

              <div className="form-group half">

                <label htmlFor="current_salary">
                  <i className="fas fa-money-bill" />
                  Current Salary (₹)
                </label>

                <input
                  type="number"
                  id="current_salary"
                  name="current_salary"
                  placeholder="e.g., 800000"
                  value={
                    formData.current_salary
                  }
                  onChange={handleInputChange}
                  disabled={isLoading}
                  min="0"
                />

              </div>

            </div>

            <div className="form-row">

              <div className="form-group half">

                <label htmlFor="expected_salary">
                  <i className="fas fa-money-bill-wave" />
                  Expected Salary (₹)
                </label>

                <input
                  type="number"
                  id="expected_salary"
                  name="expected_salary"
                  placeholder="e.g., 1000000"
                  value={
                    formData.expected_salary
                  }
                  onChange={handleInputChange}
                  disabled={isLoading}
                  min="0"
                />

              </div>

              <div className="form-group half">

                <label htmlFor="notice_period_days">
                  <i className="fas fa-clock" />
                  Notice Period
                </label>

                <select
                  id="notice_period_days"
                  name="notice_period_days"
                  value={
                    formData.notice_period_days
                  }
                  onChange={handleSelectChange}
                  disabled={isLoading}
                  className="notice-period-select"
                >

                  {NOTICE_PERIOD_OPTIONS.map(
                    (option) => (
                      <option
                        key={option.value}
                        value={option.value}
                      >
                        {option.label}
                      </option>
                    )
                  )}

                </select>

              </div>

            </div>

            <div className="form-group">

              <label htmlFor="linkedin_url">
                <i className="fab fa-linkedin" />
                LinkedIn URL
              </label>

              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                placeholder="https://linkedin.com/in/your-profile"
                value={
                  formData.linkedin_url
                }
                onChange={handleInputChange}
                disabled={isLoading}
              />

            </div>

            <div className="form-group">

              <label htmlFor="resume">
                <i className="fas fa-file-upload" />
                Resume *
              </label>

              <div className="resume-upload-wrapper">

                <input
                  type="file"
                  id="resume"
                  name="resume"
                  onChange={handleResumeChange}
                  disabled={isLoading}
                  accept=".pdf,.doc,.docx"
                  className="resume-input"
                  required
                />

                {resumeFileName ? (

                  <div className="resume-selected">

                    <i
                      className="fas fa-check-circle"
                      style={{
                        color: '#28a745'
                      }}
                    />

                    <span>
                      {resumeFileName}
                    </span>

                    <button
                      type="button"
                      className="resume-remove"
                      onClick={() => {
                        setResumeFile(null);
                        setResumeFileName('');
                      }}
                    >
                      <i className="fas fa-times" />
                    </button>

                  </div>

                ) : (

                  <div className="resume-placeholder">

                    <i className="fas fa-cloud-upload-alt" />

                    <span>
                      Click to upload your resume
                    </span>

                  </div>

                )}

              </div>

              <small>
                PDF, DOC, DOCX (Max 10MB) -
                <strong> Required</strong>
              </small>

            </div>

            <button
              type="submit"
              className="register-submit-btn"
              disabled={isLoading}
            >

              {isLoading ? (
                <>
                  <span className="spinner" />
                  Sending OTP...
                </>
              ) : (
                <>
                  <i className="fas fa-shield-alt" />
                  Register & Verify
                </>
              )}

            </button>

          </form>

        ) : (

          <div className="otp-section">

            <div className="otp-header">

              <div className="otp-icon">
                <i className="fas fa-shield-alt" />
              </div>

              <h3>
                Verify Your Mobile Number
              </h3>

              <p>
                We've sent a 6-digit OTP to{' '}
                <strong>
                  {formatMobileNumber(
                    formData.mobile
                  )}
                </strong>
              </p>

            </div>

            <form
              onSubmit={handleVerifyOtp}
              className="otp-form"
            >

              <div className="otp-input-group">

                {otp.map((digit, index) => (

                  <input
                    key={index}
                    ref={(el) =>
                      (inputRefs.current[index] =
                        el)
                    }
                    type="text"
                    maxLength="1"
                    value={digit}
                    onChange={(e) =>
                      handleOtpChange(
                        index,
                        e.target.value
                      )
                    }
                    onKeyDown={(e) =>
                      handleOtpKeyDown(
                        index,
                        e
                      )
                    }
                    onPaste={
                      index === 0
                        ? handleOtpPaste
                        : undefined
                    }
                    className="otp-input"
                    disabled={isLoading}
                    inputMode="numeric"
                    autoFocus={
                      index === 0
                    }
                  />

                ))}

              </div>

              {error && (
                <div
                  ref={errorRef}
                  className="error-message"
                >
                  <i className="fas fa-exclamation-circle" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="otp-verify-btn"
                disabled={isLoading}
              >

                {isLoading ? (
                  <>
                    <span className="spinner" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check-circle" />
                    Verify & Complete Registration
                  </>
                )}

              </button>

              <div className="otp-resend">

                <span>

                  {canResend ? (

                    <button
                      type="button"
                      onClick={
                        handleResendOtp
                      }
                      className="resend-btn"
                      disabled={isLoading}
                    >
                      Resend OTP
                    </button>

                  ) : (

                    `Resend OTP in ${timer}s`

                  )}

                </span>

              </div>

              <button
                type="button"
                className="otp-back-btn"
                onClick={() => {
                  setStep(1);
                  setError('');
                  setOtp([
                    '',
                    '',
                    '',
                    '',
                    '',
                    ''
                  ]);
                }}
                disabled={isLoading}
              >
                <i className="fas fa-arrow-left" />
                Go Back
              </button>

            </form>

          </div>

        )}

        <div className="register-footer">

          <p>
            Already have an account?{' '}
            <Link to="/login">
              Sign in
            </Link>
          </p>

        </div>

      </div>

    </div>
  );
};

export default Register;